/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// react and redux
import React, { useEffect, useState  } from "react";
import { Link, useNavigate, useParams, useOutletContext, useLocation } from "react-router-dom";
// redux
import { useDispatch, useSelector } from "react-redux";
import { selectUser } from "../../../store/userSlice";
import authRoles from "../../../auth/authRoles";
// actions
import { getListing, addInstance, updateInstance, deleteInstance } from "../../../actions";
// forms
import * as yup from "yup";
import { Controller, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup/dist/yup";
// BASE Components
import _ from "../../../../@lodash";
import BASELoading from "../../../../@BASE/core/BASELoading";
// material-ui components
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
// custom components
import FormSectionHeader from "../../../shared-components/crud/instance/FormSectionHeader";
import IDArray from "../../../shared-components/forms/IDArray";
import LessonStatusSteps from "./LessonStatusSteps";
import DeleteModal from "../../../shared-components/crud/DeleteModal";

// form validation
const schema = yup.object().shape({
  course: yup.string().required("You must select a course"),
  title: yup.string().required("You must enter a name"),
  label: yup.string().required("You must enter a label"),
  classification: yup.string().required("You must select a lesson type"),
});

const LessonForm = (props) => {
  const { instance, handleInstanceAction, loading, setOnSave, setSaveDisabled } = useOutletContext();
  const [courses, setCourses] = useState(null);
  const user = useSelector(selectUser);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const routeParams = useParams();
  const { search } = useLocation();
  const queryParams = new URLSearchParams(search);
  const course = instance?.course?.id ?? queryParams.get('p');
  // form methods
  const { control, watch, reset, handleSubmit, formState, setValue } = useForm(
    {
      mode: "onChange",
      resolver: yupResolver(schema),
    }
  );
  // form state
  const { isValid, dirtyFields, errors } = formState;
  // watch form
  const form = watch();
  // get parent listing
  const handleGetCourses = async () => {
    try {
      const response = await dispatch(getListing('courses')()).unwrap();
      setCourses(response);
      // manually set course from query param
      setValue('course', course);
    } catch (err) {
      console.log(err.message);
    }
  }
  // ensure courses are only fetched once
  useEffect(() => {
    // get users
    handleGetCourses();
  }, [dispatch, routeParams]);
  // form submission
  function onSubmit(data) {
    // dispatch add or update course
    if (routeParams.id === 'new') {
      handleInstanceAction(addInstance('lessons')(data), 'post');
    } else {
      handleInstanceAction(updateInstance('lessons')(data), 'patch');
    }
  }
  // Pass the handleSubmit function to setOnSave
  useEffect(() => {
    setOnSave(() => handleSubmit(onSubmit));
  }, []);
  // Update the parent component with the form's validity and dirty state
  useEffect(() => {
    setSaveDisabled(_.isEmpty(dirtyFields) || !isValid);
  }, [formState]);
  // handle removal of lesson
  const handleRemoveLesson = () => {
    // build request data
    const data = {
        id: instance.id,
        data: {
          user: user._id || user.id
        }
      };
    handleInstanceAction(deleteInstance('lessons')(data), 'delete');
    // navigate to projects listing
    navigate('/courses/' + course);
  }
  // on instance change, reset the form
  useEffect(() => {
    let data = {
      ...instance,
      course: course
    };
    reset({ ...data });
  }, [instance, reset]);
  // if data is missing, return loading
  if (_.isEmpty(form) || !instance || !courses) {
    return <BASELoading />;
  }

  return (
    <div className="relative flex flex-col flex-auto">
      <div className="relative my-64 px-24 sm:px-32 max-w-4xl">
        <FormSectionHeader title="Basic Information" description="Enter the basic project details below." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-4">
            <Controller
              control={control}
              name="title"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Title"
                  placeholder="Title"
                  id="title"
                  error={!!errors.title}
                  helperText={errors?.title?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="label"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Label"
                  placeholder="Label"
                  id="label"
                  error={!!errors.label}
                  helperText={errors?.label?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="classification"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Classification"
                  placeholder="Classification"
                  id="label"
                  error={!!errors.classification}
                  helperText={errors?.classification?.message}
                  variant="outlined"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
        </div>

        <Divider className="my-40" />

        <FormSectionHeader title="Lesson Details" description="Enter the lesson details below." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-2">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors.course}
              required
              disabled
            >
              <InputLabel id="project">Course</InputLabel>
              <Controller
                control={control}
                name="course"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="Course"
                    placeholder="Course"
                    id="course"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a Course</MenuItem>
                    {courses.map((course) => (
                      <MenuItem value={course.id} key={course.id} className="space-x-12">
                        <span>{course.title}</span>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="order"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Order"
                  placeholder="Order"
                  id="order"
                  error={!!errors?.order}
                  helperText={errors?.order?.message}
                  variant="outlined"
                  type="number"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
        </div>

        <Divider className="my-40" />

        <FormSectionHeader title="Linked Lesson(s)" description="Enter linked lesson IDs below." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-4">
            <Controller
              control={control}
              name="linked_lesson_list"
              render={({ field }) => (
                <IDArray
                  label="Linked Lesson"
                  {...{ control, errors, watch }}
                />
              )}
            />
          </div>
        </div>

        <Divider className="my-40" />

        <FormSectionHeader title="Lesson Status" description="These values are here for progress and status." />
        <div className="mt-32 grid w-full gap-24 sm:grid-cols-4">
          <div className="sm:col-span-4">
            <Typography variant='body2' className='font-semibold'>Progress:</Typography>
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="progress.development"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="Storyboard Progress"
                  placeholder="Storyboard Progress"
                  id="progress_development"
                  error={!!errors?.progress?.development}
                  helperText={errors?.progress?.development?.message}
                  variant="outlined"
                  type="number"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="progress.production"
              render={({ field }) => (
                <TextField
                  {...field}
                  label="IMI Progress"
                  placeholder="IMI Progress"
                  id="progress_production"
                  error={!!errors?.progress?.production}
                  helperText={errors?.progress?.production?.message}
                  variant="outlined"
                  type="number"
                  required
                  fullWidth
                  size="small"
                />
              )}
            />
          </div>
          <div className="sm:col-span-4">
            <Typography variant='body2' className='font-semibold'>Status:</Typography>
          </div>
          <div className="sm:col-span-2">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors?.status?.development}
              required
            >
              <InputLabel id="status_development">Storyboard Status</InputLabel>
              <Controller
                control={control}
                name="status.development"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="Storyboard Status"
                    placeholder="Storyboard Status"
                    id="status_development"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a Status</MenuItem>
                    {Object.keys(LessonStatusSteps).map((key) => (
                      <MenuItem value={key} key={key} className="space-x-12">
                        <span>{LessonStatusSteps[key].label}</span>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
          <div className="sm:col-span-2">
            <FormControl
              variant="outlined"
              fullWidth
              error={!!errors?.status?.production}
              required
            >
              <InputLabel id="status_production">IMI Status</InputLabel>
              <Controller
                control={control}
                name="status.production"
                defaultValue=""
                render={({ field: { onChange, value } }) => (
                  <Select
                    value={value}
                    onChange={(e) => {
                      onChange(e);
                    }}
                    label="Storyboard Status"
                    placeholder="Storyboard Status"
                    id="status_production"
                    variant="outlined"
                    required
                    fullWidth
                    size="small"
                  >
                    <MenuItem value="" disabled>Select a Status</MenuItem>
                    {Object.keys(LessonStatusSteps).map((key) => (
                      <MenuItem value={key} key={key} className="space-x-12">
                        <span>{LessonStatusSteps[key].label}</span>
                      </MenuItem>
                    ))}
                  </Select>
                )}
              />
            </FormControl>
          </div>
        </div>  
      </div>

      <Box
        className="flex items-center mt-40 py-14 pr-16 pl-4 sm:pr-48 sm:pl-36 border-t"
        sx={{ backgroundColor: 'background.default' }}
      >
        {routeParams.id !== 'new' && (
          authRoles.admin.includes(user.role) && (
            <DeleteModal onDelete={handleRemoveLesson} resource="lesson" />
          )
        )}
        <Button
          component={Link}
          className="ml-auto"
          to="../"
        >
          Cancel
        </Button>
        <Button
          className="ml-8"
          variant="contained"
          color="secondary"
          disabled={_.isEmpty(dirtyFields) || !isValid}
          onClick={handleSubmit(onSubmit)}
        >
          Save
        </Button>
      </Box>
    </div>
  );
};

export default LessonForm;
